import React, { useState, useEffect } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';
import { Helmet } from "react-helmet-async";
const ProductEngineering = () => {
  const phrases = [
    "Digital Innovation",
    "Product Engineering"
  ];

  const cardData = [
    { type: 'purple-clay', content: '📐' },
    { type: 'smooth-wave', content: '🚀' },
    { type: 'abstract-blocks', content: '🧩' },
    { type: 'pink-sphere', content: '🔬' }
  ];

  const [currentPhraseIndex, setCurrentPhraseIndex] = useState(0);
  const [displayedText, setDisplayedText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [cardIndex, setCardIndex] = useState(0);

  useEffect(() => {
    const currentPhrase = phrases[currentPhraseIndex];
    let timer;

    const handleTyping = () => {
      if (!isDeleting) {
        setDisplayedText(currentPhrase.substring(0, displayedText.length + 1));
        if (displayedText === currentPhrase) {
          timer = setTimeout(() => setIsDeleting(true), 1600);
          return;
        }
      } else {
        setDisplayedText(currentPhrase.substring(0, displayedText.length - 1));
        if (displayedText === '') {
          setIsDeleting(false);
          setCurrentPhraseIndex((prev) => (prev + 1) % phrases.length);
          return;
        }
      }

      const speed = isDeleting ? 30 : 80;
      timer = setTimeout(handleTyping, speed);
    };

    timer = setTimeout(handleTyping, 100);
    return () => clearTimeout(timer);
  }, [displayedText, isDeleting, currentPhraseIndex]);

  useEffect(() => {
    const cardTimer = setInterval(() => {
      setCardIndex((prev) => (prev + 1) % cardData.length);
    }, 1100);
    return () => clearInterval(cardTimer);
  }, []);

  // Intersection Observer for Scroll Animations
  useEffect(() => {
    const observerOptions = {
      root: null,
      rootMargin: '0px',
      threshold: 0.15
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-visible');
        }
      });
    }, observerOptions);

    const animatedElements = document.querySelectorAll(
      '.scroll-anim-fade-in, .scroll-anim-fade-in-up, .scroll-anim-fade-in-left, .scroll-anim-fade-in-right, .scroll-anim-flip, .scroll-anim-zoom-in'
    );

    animatedElements.forEach((el) => observer.observe(el));

    return () => {
      animatedElements.forEach((el) => observer.unobserve(el));
    };
  }, []);

  // Content Data
  const coreServices = [
    { icon: "📐", title: "New Product Concepting & Strategy", desc: "Translate high-level concepts into rigorous technical specifications, feature roadmaps, and cross-platform architectural blueprints." },
    { icon: "🚀", title: "MVP & Rapid Prototype Development", desc: "Build functional, market-ready minimum viable products quickly to test user engagement and secure early traction." },
    { icon: "🎨", title: "UI/UX Experience Engineering", desc: "Blend customer empathy with performance design, creating unified user interfaces, interaction tokens, and dynamic layouts." },
    { icon: "🧩", title: "Modular Architecture & Core Engines", desc: "Structure decoupled micro-frontends and robust application layers engineered to easily support features as you scale." },
    { icon: "🔌", title: "Hardware-Software Co-Design & IoT", desc: "Bridge digital platforms with physical hardware hubs, embedded firmware setups, and real-time machine telemetry links." },
    { icon: "🔄", title: "Product Evolution & Refactoring", desc: "Modernize aging digital platforms into cloud-native architectures without introducing performance gaps or missing steps." },
    { icon: "🛡️", title: "Next-Gen Product Security Hardening", desc: "Integrate biometric authentication structures, secure communication tokens, and strict data safeguards directly into your build." }
  ];

  const advancedServices = [
    { icon: "🔬", title: "A/B Testing & Telemetry Ingestion", desc: "Configure high-fidelity monitoring layers to track operational usage and capture precise user behavior trends automatically." },
    { icon: "☁️", title: "Cloud-Native Product Assemblies", desc: "Deploy products onto highly elastic serverless backbones utilizing auto-scaling groups and global edge caching." },
    { icon: "🤖", title: "Intelligent Feature Infusion", desc: "Embed customized machine learning classifiers, recommendation models, and natural language filters seamlessly." },
    { icon: "🗄️", title: "Scalable Data & Event Schemas", desc: "Architect hybrid database engines capable of handling sudden transactional bursts and fluid customer growth." },
    { icon: "📦", title: "Continuous Feature Delivery Pipelines", desc: "Enforce error-free software releases using declarative continuous integration scripts that minimize deployment friction." },
    { icon: "🏁", title: "Cross-Device Performance Audits", desc: "Stress-test layouts against hardware form factors to guarantee lightning-fast response times globally." },
    { icon: "⚖️", title: "Compliance-First Architecture", desc: "Align codebases with complex regulatory standards, securing user data rules natively across nodes." },
    { icon: "🔗", title: "Ecosystem Integration Frameworks", desc: "Connect your core products smoothly with external enterprise tools using highly reliable webhooks and custom adaptors." },
    { icon: "🧪", title: "Automated Regression Harnesses", desc: "Construct complete behavioral simulation tests to safeguard the core user journey during major feature upgrades." }
  ];

  const technologies = {
    "Design & Prototyping": ["Figma / Adobe XD Suite", "Storybook UI Components", "ProtoPie / InVision Frameworks", "Tailwind Design Tokens"],
    "Core Development Stack": ["React.js / Next.js / TypeScript", "Node.js (NestJS / Express)", "Python (FastAPI / Django)", "Go / Rust Core Systems"],
    "Mobile & Edge Tech": ["React Native / Flutter", "Swift / Kotlin Native", "WebAssembly (Wasm) Assemblies", "Embedded C / Firmware"],
    "Data & Analytics Hubs": ["PostgreSQL / MongoDB Core", "GraphQL APIs / Apollo Hubs", "Mixpanel / Segment Ingestion", "Redis High-Speed Caching"],
    "Cloud & Product DevOps": ["AWS / GCP Cloud Infra", "Docker / Kubernetes Orchestration", "GitHub Actions Automation", "Terraform Infrastructure-as-Code"]
  };

  const processSteps = [
    { num: "01", title: "Product Ideation & Discovery", desc: "Mapping user personas, assessing technical risk vectors, and establishing baseline performance milestones." },
    { num: "02", title: "UX Wireframing & Prototyping", desc: "Designing interactive screens, validating usability flows, and testing structural user interface tokens." },
    { num: "03", title: "Agile Feature Engineering", desc: "Writing clean, modular components alongside robust APIs in highly iterative product development sprints." },
    { num: "04", title: "System Assembly & Synthesis", desc: "Unifying front-end client dashboards with scalable cloud backbones and third-party tools." },
    { num: "05", title: "Rigorous QA & Cross-Device Runs", desc: "Executing complete behavioral simulations, load stress tests, and automated regression checks." },
    { num: "06", title: "Global Market Launch", desc: "Publishing production builds onto high-availability cloud infrastructure with zero operational disruption." },
    { num: "07", title: "Telemetry & Product Evolution", desc: "Analyzing real-world usage patterns, refining slow feature gates, and rolling out seamless upgrades." }
  ];

  const industries = [
    "High-Growth SaaS Startups", "E-Commerce Platforms & Malls", "FinTech & Digital Banking", "Healthcare & Smart Wearables",
    "EdTech & Virtual Classrooms", "Logistics & Fleet Telematics", "Industrial Internet of Things (IIoT)", "PropTech & Real Estate Systems",
    "Entertainment & Media Streaming", "Automotive & Smart Mobility", "Enterprise Product Ecoystems", "Retail Tech & Direct-to-Consumer"
  ];

  const designBenefits = [
    "Sub-Second Page Load Speeds", "Highly Flexible Modular Layouts", "Elastic Scale Across Regions",
    "Airtight Zero-Trust Boundaries", "Data-Driven Strategy Validation", "Rapid Market Integration Speeds",
    "Standardized Clean Code Structures", "Total Intellectual Property Transfer"
  ];

  const whyChooseUs = [
    "Certified Product Engineers", "Proven MVP-to-Scale Frameworks", "Modern UI/UX Performance Focus",
    "Deep Cross-Platform Engineering Expertise", "Security-First Target Node Hardening", "Optimized Workload Density Layouts",
    "Comprehensive Post-Launch Maintenance Support", "Transparent Cost Optimization Models", "Enterprise-Grade SLA Commitments",
    "Experienced Cloud-Native Designers", "Native Collaborative Development Cycles", "100% Core System Stability Commitments"
  ];

  return (
    <>
      <Helmet>
        {/* Primary SEO */}
        <title>
          Product Engineering Services | Product Design & Software Engineering | Affsoft IT Solutions Pvt. Ltd.
        </title>

        <meta
          name="title"
          content="Product Engineering Services | Product Design & Software Engineering | Affsoft IT Solutions Pvt. Ltd."
        />

        <meta
          name="description"
          content="Affsoft IT Solutions Pvt. Ltd. provides end-to-end product engineering services, including product design, software product development, product modernization, cloud engineering, DevOps, QA testing, UI/UX design, and digital product lifecycle management."
        />

        <meta
          name="keywords"
          content="Product Engineering, Product Engineering Services, Software Product Development, Product Design, Digital Product Development, Product Modernization, Product Lifecycle Management, Cloud Engineering, DevOps Services, Quality Assurance Testing, UI UX Design, Product Innovation, SaaS Product Development, Enterprise Product Development, Software Engineering, Product Engineering Company, Affsoft IT Solutions Pvt. Ltd."
        />

        <meta name="robots" content="index, follow" />
        <meta name="author" content="Affsoft IT Solutions Pvt. Ltd." />
        <meta name="language" content="English" />
        <meta name="theme-color" content="#0d5c53" />

        {/* Canonical */}
        <link
          rel="canonical"
          href="https://www.affsoft.com/product-engineering"
        />

        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta
          property="og:site_name"
          content="Affsoft IT Solutions Pvt. Ltd."
        />
        <meta
          property="og:title"
          content="Professional Product Engineering Services | Affsoft IT Solutions Pvt. Ltd."
        />
        <meta
          property="og:description"
          content="Accelerate innovation with product engineering services from Affsoft IT Solutions Pvt. Ltd. We specialize in product design, software development, cloud engineering, DevOps, QA testing, and product modernization."
        />
        <meta
          property="og:url"
          content="https://www.affsoft.com/product-engineering"
        />
        <meta
          property="og:image"
          content="https://www.affsoft.com/images/affsoft-logo.png"
        />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="Product Engineering Services | Product Design & Software Engineering | Affsoft IT Solutions Pvt. Ltd."
        />
        <meta
          name="twitter:description"
          content="Build innovative digital products with product engineering services from Affsoft IT Solutions Pvt. Ltd. We offer product design, software development, cloud engineering, DevOps, QA testing, and lifecycle management."
        />
        <meta
          name="twitter:image"
          content="https://www.affsoft.com/images/affsoft-logo.png"
        />
      </Helmet>
      <div className="product-engineering-services-page bg-light" style={{ fontFamily: '"Inter", sans-serif', overflowX: 'hidden' }}>

        {/* ================= HERO SECTION ================= */}
        <div
          className="hero-container container-fluid d-flex align-items-center justify-content-center position-relative overflow-hidden"
          style={{
            backgroundImage: "url('./images/home.jpg')",
            backgroundRepeat: 'no-repeat',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            minHeight: '85vh',
            backgroundColor: '#0f0e1c' // dark digital engineering context fallback
          }}
        >
          {/* Background Grid Lines */}
          <div className="grid-overlay position-absolute top-0 start-0 w-100 h-100" style={{ opacity: 0.15 }}>
            <div className="grid-line line-v-1"></div>
            <div className="grid-line line-v-2"></div>
            <div className="grid-line line-v-3"></div>
          </div>

          {/* Infinite Slow-Scrolling Marquee Text Background Wrapper */}
          <div className="marquee-wrapper select-none position-absolute w-100 scroll-anim-fade-in" style={{ top: '15%', opacity: 0.05 }}>

          </div>

          <div className="marquee-wrapper select-none position-absolute w-100 scroll-anim-fade-in">
            <div className="marquee-content text-white">
              <span>Digital Innovation &nbsp; and &nbsp;</span>
              <span>Product Engineering </span>
            </div>
          </div>

          {/* Main Content Layout */}
          <div className="container position-relative z-3 my-5">
            <div className="row align-items-center py-4">

              {/* Left Side Info */}
              <div className="col-12 col-md-5 text-start d-flex flex-column align-items-start hero-left-pane text-white mb-4 mb-md-0 scroll-anim-fade-in-left">
                <div className="asterisk-icon mb-3 text-primary fs-3">✳</div>
                <h1 className="fw-bold mb-3 display-5">Product Engineering</h1>
                <p className="tagline mb-4 text-white" style={{ fontSize: '1rem', lineHeight: '1.6', opacity: 0.9 }}>
                  At Affsoft IT Solutions Pvt. Ltd., we turn bold market visions into elite, consumer-grade digital products. Our comprehensive engineering solutions combine modern user experiences, highly scalable software structures, and continuous performance telemetry to scale your product smoothly and delight your users.
                </p>
                <button className="btn btn-outline-light d-flex align-items-center gap-2 px-4 py-2 rounded-pill fw-semibold">
                  <span>➔</span>
                  <span>About Affsoft</span>
                </button>
              </div>

              {/* Center Section */}
              <div className="col-12 col-md-4 text-center mb-4 mb-md-0 hero-center-pane scroll-anim-flip">
                <div className="typing-badge px-4 py-3 mx-auto bg-dark bg-opacity-75 rounded-pill border border-secondary shadow-lg d-inline-block">
                  <span className="typed-text text-white fw-mono fs-4">{displayedText}</span>
                  <span className="cursor text-primary fs-4 animate-blink">|</span>
                </div>
              </div>

              {/* Right Section */}
              <div className="col-12 col-md-3 d-flex justify-content-center justify-content-md-end hero-right-pane scroll-anim-fade-in-right">
                <div className="glass-card-frame shadow-lg p-3 bg-white bg-opacity-10 rounded-4 border border-light" style={{ width: '220px', backdropFilter: 'blur(10px)' }}>
                  <div className={`inner-graphic-card graphic-${cardData[cardIndex].type} d-flex align-items-center justify-content-center rounded-3 bg-dark`} style={{ height: '180px' }}>
                    <span className="graphic-icon display-3">{cardData[cardIndex].content}</span>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </div>

        {/* ================= CORE SOLUTIONS SECTION ================= */}
        <section className="py-5 bg-white">
          <div className="container">
            <div className="text-center max-width-700 mx-auto mb-5 scroll-anim-fade-in-up">
              <span className="badge bg-primary px-3 py-2 rounded-pill mb-2 text-uppercase">Market Innovation</span>
              <h2 className="fw-bold text-dark display-6">Resilient Digital Architectures Programmed for Modern Scale</h2>
              <p className="text-muted mt-3">
                Standard corporate software tools often fail to adapt to fluid market demands and shifting user expectations. At Affsoft IT Solutions Pvt. Ltd., we construct flexible, feature-rich product designs that give you total strategic control.
              </p>
            </div>

            <div className="row g-4">
              {coreServices.map((service, index) => (
                <div className="col-12 col-md-6 col-lg-4 scroll-anim-zoom-in" key={index} style={{ animationDelay: `${index * 0.1}s` }}>
                  <div className="card h-100 shadow-sm border-0 rounded-4 p-4 hover-translate transition-all">
                    <div className="fs-1 mb-3 text-primary">{service.icon}</div>
                    <h4 className="fw-bold fs-5 mb-2 text-dark">{service.title}</h4>
                    <p className="text-muted mb-0 small">{service.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Product Optimization Benefits Checklist */}
            <div className="mt-5 p-4 rounded-4 bg-light border scroll-anim-fade-in-up">
              <h5 className="fw-bold text-center text-dark mb-4">Why Invest in Dedicated Product Engineering Solutions?</h5>
              <div className="row g-3">
                {designBenefits.map((benefit, index) => (
                  <div className="col-6 col-md-3 d-flex align-items-center gap-2" key={index}>
                    <span className="text-success fs-5">✓</span>
                    <span className="text-secondary small fw-medium">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ================= ADVANCED MODERNIZATION OPTIONS ================= */}
        <section className="py-5 bg-light">
          <div className="container">
            <div className="text-center max-width-700 mx-auto mb-5 scroll-anim-fade-in-up">
              <span className="badge bg-primary px-3 py-2 rounded-pill mb-2 text-uppercase">Intelligent Layers</span>
              <h2 className="fw-bold text-dark display-6">Elite Software Systems Engineered for High Visibility</h2>
              <p className="text-muted mt-3">
                We extend out-of-the-box user interfaces into deeply optimized engineering environments. From processing low-latency metric loops to structuring scalable microservices, our setups deliver high-efficiency clusters that protect your product lifecycle.
              </p>
            </div>

            <div className="row g-4">
              {advancedServices.map((service, index) => (
                <div className="col-12 col-md-6 col-lg-4 scroll-anim-zoom-in" key={index} style={{ animationDelay: `${index * 0.1}s` }}>
                  <div className="card h-100 shadow-sm border-0 rounded-4 p-4 bg-white hover-translate transition-all">
                    <div className="fs-1 mb-3 text-primary">{service.icon}</div>
                    <h4 className="fw-bold fs-5 mb-2 text-dark">{service.title}</h4>
                    <p className="text-muted mb-0 small">{service.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ================= TECHNOLOGIES SECTION ================= */}
        <section className="py-5 bg-dark text-white">
          <div className="container">
            <div className="text-center mb-5 scroll-anim-fade-in-up">
              <span className="badge bg-warning text-dark px-3 py-2 rounded-pill mb-2 text-uppercase fw-bold">Ecosystem Stack</span>
              <h2 className="fw-bold display-6">Frameworks & Modern Design Libraries We Leverage</h2>
              <p className="text-light-50">We structure, deploy, and monitor complex application networks using premium open-source tool sets.</p>
            </div>

            <div className="row g-4 justify-content-center">
              {Object.entries(technologies).map(([category, list], index) => (
                <div className="col-12 col-sm-6 col-md-4 col-lg scroll-anim-fade-in" key={index}>
                  <div className="bg-secondary bg-opacity-25 p-4 rounded-4 h-100 border border-secondary border-opacity-50">
                    <h5 className="fw-bold text-warning mb-3 border-bottom border-secondary pb-2">{category}</h5>
                    <ul className="list-unstyled mb-0">
                      {list.map((tech, i) => (
                        <li key={i} className="mb-2 d-flex align-items-center gap-2 small">
                          <span className="text-warning opacity-75">•</span> {tech}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ================= ROADMAP PROCESS ================= */}
        <section className="py-5 bg-white">
          <div className="container">
            <div className="text-center mb-5 scroll-anim-fade-in-up">
              <span className="badge bg-secondary px-3 py-2 rounded-pill mb-2 text-uppercase">Lifecycle Architecture</span>
              <h2 className="fw-bold text-dark display-6">Our Engineered Product Roadmap</h2>
              <p className="text-muted">A clear, disciplined framework transforming raw blueprints securely into high-performing, production-grade applications.</p>
            </div>

            <div className="row g-3">
              {processSteps.map((step, index) => (
                <div className="col-12 col-md-6 col-lg scroll-anim-fade-in-up" key={index} style={{ animationDelay: `${index * 0.15}s` }}>
                  <div className="p-4 rounded-4 bg-light border-start border-primary border-4 h-100 shadow-sm">
                    <div className="fw-bold text-primary display-6 mb-2">{step.num}</div>
                    <h5 className="fw-bold text-dark fs-6">{step.title}</h5>
                    <p className="text-muted small mb-0">{step.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ================= INDUSTRIES SERVED ================= */}
        <section className="py-5 bg-light">
          <div className="container text-center">
            <h2 className="fw-bold text-dark mb-4 display-6 scroll-anim-fade-in-up">Industries We Empower</h2>
            <div className="d-flex flex-wrap justify-content-center gap-2 max-width-900 mx-auto scroll-anim-zoom-in">
              {industries.map((industry, index) => (
                <span className="badge bg-white text-dark border px-3 py-2 rounded-pill shadow-sm fs-7 fw-medium" key={index}>
                  🏢 {industry}
                </span>
              ))}
            </div>
          </div>
        </section>

        {/* ================= WHY CHOOSE AFFSOFT ================= */}
        <section className="py-5 bg-white border-top">
          <div className="container">
            <div className="text-center mb-5 scroll-anim-fade-in-up">
              <h2 className="fw-bold text-dark display-6">Why Partner with Affsoft IT Solutions Pvt. Ltd.?</h2>
              <p className="text-muted">We combine rigorous behavioral automated validation with expert cloud architecture to deploy secure systems safely.</p>
            </div>

            <div className="row g-3">
              {whyChooseUs.map((reason, index) => (
                <div className="col-12 col-md-6 col-lg-4 scroll-anim-fade-in-left" key={index} style={{ animationDelay: `${(index % 3) * 0.1}s` }}>
                  <div className="d-flex align-items-center gap-3 p-3 bg-light rounded-3 h-100">
                    <span className="text-primary fs-4">🛡️</span>
                    <span className="text-dark fw-semibold small">{reason}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Custom Styles Injection */}
        <style>{`
        .hover-translate {
          transition: transform 0.2s ease, box-shadow 0.2s ease;
        }
        .hover-translate:hover {
          transform: translateY(-5px);
          box-shadow: 0 10px 20px rgba(0,0,0,0.1) !important;
        }
        .text-light-50 {
          color: rgba(255, 255, 255, 0.7);
        }
        .max-width-700 {
          max-width: 700px;
        }
        .max-width-900 {
          max-width: 900px;
        }
        .animate-blink {
          animation: blink 1s step-end infinite;
        }
        @keyframes blink {
          from, to { color: transparent }
          50% { color: inherit }
        }

        /* --- Scroll Animation Core --- */
        .scroll-anim-fade-in,
        .scroll-anim-fade-in-up,
        .scroll-anim-fade-in-left,
        .scroll-anim-fade-in-right,
        .scroll-anim-flip,
        .scroll-anim-zoom-in {
          opacity: 0;
          will-change: transform, opacity;
          transition: all 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94);
        }

        /* Visibility Trigger via IntersectionObserver */
        .animate-visible {
          opacity: 1 !important;
          transform: none !important;
        }

        /* --- Animations Configurations --- */
        .scroll-anim-fade-in-up {
          transform: translateY(40px);
        }
        .scroll-anim-fade-in-left {
          transform: translateX(-50px);
        }
        .scroll-anim-fade-in-right {
          transform: translateX(50px);
        }
        .scroll-anim-zoom-in {
          transform: scale(0.85);
        }
        .scroll-anim-flip {
          transform: perspective(400px) rotateY(90deg);
          transition: all 1s cubic-bezier(0.175, 0.885, 0.32, 1.275);
        }
        
        .scroll-anim-flip.animate-visible {
          transform: perspective(400px) rotateY(0deg) !important;
        }
      `}</style>

      </div>
    </>
  );
};

export default ProductEngineering;